import { CircleMinus, Clock, SquareCheck, X } from 'lucide-react';

import { SkeletonRows, StatusChip, type StatusTone } from '~/components/ui';
import { errJsonMessage, type ErrJson } from '~/lib/apiError';

export type StepStatus = {
  isSkipped?: boolean;
  isOk?: boolean;
  result?: ErrJson & { isErr?: boolean };
};

/** The harness progress / skeleton shape the setup modals render (domain setup and onboarding). */
export type HarnessProgress = {
  statusesOfSteps?: Record<string, StepStatus>;
  isOk?: boolean;
  isDone?: boolean;
  isRunning?: boolean;
};

const STATUS_TONE: Record<string, StatusTone> = {
  Skipped: 'warning',
  Failed: 'error',
  Success: 'success',
  Pending: 'neutral',
};

export const DomainCard = ({ displayLoop }: { displayLoop?: HarnessProgress }) => {
  if (!displayLoop || Object.keys(displayLoop).length === 0) {
    return <SkeletonRows />;
  }

  const steps = displayLoop.statusesOfSteps ?? {};

  return (
    <ol className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
      {Object.keys(steps).map((item, index) => {
        const step = steps[item];
        const status = step?.isSkipped
          ? 'Skipped'
          : step?.result?.isErr
            ? 'Failed'
            : step?.isOk
              ? 'Success'
              : 'Pending';
        // The step's error is why the operator is looking at this card; show it instead of a
        // bare red X. (Optional steps that are expected to fail arrive as skipped, not failed.)
        const reason =
          step?.result?.isErr || step?.isSkipped ? errJsonMessage(step.result) : undefined;
        return (
          <li
            key={index}
            className="border-b border-gray-100 px-4 py-3 text-sm text-gray-800 last:border-b-0 dark:border-white/[0.04] dark:text-white/90"
          >
            <div className="flex items-center justify-between">
              <span>{item}</span>
              <span className="flex items-center gap-2">
                <StatusChip label={status} tone={STATUS_TONE[status]} />
                {step?.isSkipped ? (
                  <CircleMinus
                    className="size-5 text-warning-600 dark:text-warning-400"
                    aria-hidden="true"
                  />
                ) : step?.result?.isErr ? (
                  <X className="size-5 text-error-600 dark:text-error-400" aria-hidden="true" />
                ) : step?.isOk ? (
                  <SquareCheck
                    className="size-5 text-success-600 dark:text-success-500"
                    aria-hidden="true"
                  />
                ) : (
                  <Clock className="size-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                )}
              </span>
            </div>
            {reason && (
              <p
                className={`mt-1 wrap-break-word text-xs ${step?.isSkipped ? 'text-warning-600 dark:text-warning-400' : 'text-error-600 dark:text-error-400'}`}
              >
                {reason}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};
